import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Eyebrow, Display } from '@/components/ui/Typography';
import { resetPasswordSchema, type ResetPasswordFormData } from '@/lib/validation';
import { friendlyMessage } from '@/lib/errors';
import { useKeyboardViewport } from '@/hooks/useKeyboardViewport';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({ resolver: zodResolver(resetPasswordSchema) });

  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { kbOpen, vvHeight } = useKeyboardViewport();

  async function onSubmit(data: ResetPasswordFormData) {
    if (!token) {
      setApiError('Link inválido. Solicite um novo link de recuperação.');
      return;
    }
    setApiError(null);
    try {
      await authApi.resetPassword(token, data.new_password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setApiError(friendlyMessage(err, 'Não foi possível redefinir a senha.'));
    }
  }

  const isTokenMissing = !token;

  // NSS (design/no-scroll/nss-auth.jsx · ForgotResetNS): mesma estrutura do
  // ForgotPassword — viewport fixa, espaçador flexível, CTA ancorado.
  return (
    <div
      className="mx-auto flex max-w-sm flex-col overflow-hidden px-7"
      style={{
        height: kbOpen && vvHeight !== null ? `${vvHeight}px` : '100dvh',
        paddingTop: kbOpen ? 10 : 'max(2rem, env(safe-area-inset-top))',
        paddingBottom: kbOpen ? 10 : 'max(1.75rem, env(safe-area-inset-bottom))'
      }}
    >
      <div
        className={`flex flex-shrink-0 items-center ${
          kbOpen ? 'mb-3' : 'mb-8 [@media(max-height:740px)]:mb-5'
        }`}
      >
        <img
          src="/logo/logo-bow-01.png"
          alt="Bow"
          className={kbOpen ? 'h-7 w-auto' : 'h-9 w-auto'}
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <Eyebrow>Recuperar acesso</Eyebrow>
      <Display
        size={36}
        className={`mt-3 ${
          kbOpen
            ? '!text-[24px] !leading-tight mt-1'
            : '[@media(max-height:740px)]:!text-[28px]'
        }`}
      >
        Nova
        <br />
        <span className="text-bow-green">senha</span>
      </Display>

      {isTokenMissing ? (
        <>
        <div className="min-h-4 flex-1" />
        <div className="flex flex-col gap-4">
          <div className="rounded-3xl bg-bow-red/10 p-5">
            <p className="text-sm font-semibold text-bow-red">Link inválido</p>
            <p className="mt-1 text-sm leading-relaxed text-bow-navy/70">
              Este link de recuperação é inválido ou expirou.
            </p>
          </div>
          <Button onClick={() => navigate('/forgot-password')}>
            Solicitar novo link
          </Button>
        </div>
        </>
      ) : done ? (
        <>
        <div className="min-h-4 flex-1" />
        <div className="flex flex-col gap-4">
          <div className="rounded-3xl bg-bow-green/10 p-5">
            <p className="text-sm font-semibold text-bow-navy">Senha redefinida!</p>
            <p className="mt-1 text-sm leading-relaxed text-bow-navy/70">
              Sua senha foi atualizada com sucesso. Redirecionando para o login…
            </p>
          </div>
        </div>
        </>
      ) : (
        /* Campos logo abaixo do hero — input visível no focus = iOS não pana
           a página (mesmo fix do passo de e-mail do Login, 2026-07-04). */
        <div className="flex min-h-0 flex-1 flex-col gap-4 pt-5">
          <p className="text-sm leading-relaxed text-bow-royal/60">
            Crie uma nova senha com pelo menos 8 caracteres, incluindo letras e números.
          </p>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex min-h-0 flex-1 flex-col gap-3"
          >
            <Input
              label="Nova senha"
              type="password"
              autoComplete="new-password"
              error={errors.new_password?.message}
              {...register('new_password')}
            />
            <Input
              label="Confirmar nova senha"
              type="password"
              autoComplete="new-password"
              error={errors.confirm_password?.message}
              {...register('confirm_password')}
            />

            {apiError && (
              <div className="rounded-2xl bg-bow-red/10 p-3">
                <p className="text-sm text-bow-red">{apiError}</p>
                {(apiError.includes('expirou') || apiError.includes('inválido')) && (
                  <Link
                    to="/forgot-password"
                    className="mt-1 block text-sm font-semibold text-bow-navy"
                  >
                    Solicitar novo link →
                  </Link>
                )}
              </div>
            )}

            <div className="min-h-2 flex-1" />
            <Button
              type="submit"
              disabled={isSubmitting}
              onPointerDown={(e) => e.preventDefault()}
            >
              {isSubmitting ? 'Salvando…' : 'Redefinir senha'}
            </Button>
          </form>

          <p
            className={`text-center text-[13px] text-bow-royal/55 ${
              kbOpen ? 'hidden' : ''
            }`}
          >
            <Link to="/login" className="font-semibold text-bow-royal">
              Voltar ao login
            </Link>
          </p>
        </div>
      )}
      </div>
    </div>
  );
}
